import CertificateElement from "./certificateElement";
import {RelationType} from "~/models/relationType";
import {certificateElementType} from "~/enums/certificateElementType";
import Certificate from "./certificate";
import {CertificateSize} from "../enums/certificateSize";
import {CertificateOrientation} from "../enums/certificateOrientation";
import {ObjectHelper} from "~/helpers/objectHelper";
import {CertificateFormat} from "../enums/certificateFormat";
import PageCertificateElementValidation from "~/models/navigableValidations/pageCertificateElementValidator";
import {IConfig, IDimensions, IPageBound} from "~/interfaces/certificateBuilderInterfaces";
import CertificateElementHandler from "~/services/certificateElementHandler";

export default class PageCertificateElement extends CertificateElement {
  highlight: boolean = false;
  _certificateElementHandler = new CertificateElementHandler(this, CertificateElement, 'certificate_elements');
  get content_type(): certificateElementType{
    return this.type
  }

  static get baseConfigs(): IConfig {
    return {
      backgroundColor: 'white',
      borderRadius: 0,
      boxShadow: '0px 0px 15px #c4c4c4;',
    };
  }

  get centerCoordinates(){
    const coords = {x: null, y: null};
    const baseX = this.pageBounds[0].x;
    const baseY = this.pageBounds[0].y;
    coords.x = baseX + this.dimensions.width / 2;
    coords.y = baseY + this.dimensions.height / 2;
    return coords;
  }

  get newElementCoordinates(){
    return this.centerCoordinates
  }

  _validation: PageCertificateElementValidation = new PageCertificateElementValidation(this);

  static shiftPageElements(pageElement: PageCertificateElement, direction: 'up' | 'down') {
    const pageShift = pageElement.dimensions.height + Certificate.pageMargin;
    if (direction === "up") {
      for (const element of pageElement.certificateElementsArray) {
        element.y -= pageShift;
      }
    } else {
      for (const element of pageElement.certificateElementsArray) {
        element.y += pageShift;
      }
    }
  }
  get marginInPixels(): number{
    return this.certificate.marginInPixels
  }

  get certificateElementsArray() {
    return ObjectHelper.toArray(this.certificate_elements);
  }

  get sortedCertificateElementsArray() {
    return this.certificateElementsArray.sort((a, b) => a.sort_order > b.sort_order ? 1 : -1);
  }

  get EMBEDDED_MODELS(): { [p: string]: { type: any; relationType: RelationType } } {
    return {
      'certificate_elements': {type: CertificateElement, relationType: RelationType.dictionary},
    };
  }

  get dimensions(): IDimensions {
    const scale = Certificate.cardScale;
    let width = 0, height = 0;
    let basePixels;
    if (this.certificate.size === CertificateSize.Card) {
      basePixels = [Certificate.cardPixelDimensions.long * scale, Certificate.cardPixelDimensions.short * scale];
    } else {
      basePixels = [Certificate.letterPixelDimensions.long, Certificate.letterPixelDimensions.short];
    }
    this.certificate.orientation === CertificateOrientation.Landscape ? [width, height] = basePixels : [height, width] = basePixels;
    return { width, height };
  }

  get downloadPageDimensions(): IDimensions {
    let baseWidth = this.dimensions.width
    let baseHeight = this.dimensions.height
    if (this.certificate.size === CertificateSize.Card && this.certificate.format === CertificateFormat.FoldOver) {
      if (this.certificate.orientation === CertificateOrientation.Landscape) {
        baseHeight *= 2
      } else {
        baseWidth *= 2
      }
    }
    return {width: baseWidth, height: baseHeight}
  }

  get pageIndex(): number {
    return this.certificate.pageElements.indexOf(this);
  }

  get pageNumber(): number {
    return this.pageIndex + 1;
  }

  // [topLeftCorner, bottomRightCorner]
  // Page bounds in edit mode: rendered with spacing between pages and other elements
  get pageBounds(): IPageBound[] {
    if (this.certificate.size === CertificateSize.Letter) {
      return [
        { x: <number>this.xValue, y: <number>this.yValue },
        { x: <number>this.xValue + this.dimensions.width, y: <number>this.yValue + this.dimensions.height }
      ]
    }
    else {
      if (this.certificate.orientation === CertificateOrientation.Portrait){
        return [
          { x: <number>this.xValue[0], y: <number>this.yValue[0] },
          { x: <number>this.xValue[1] + (this.dimensions.width) + Certificate.pageMargin, y: <number>this.yValue[1] + this.dimensions.height }
        ]
      } else {
        return [
          { x: <number>this.xValue[0], y: <number>this.yValue[0] },
          { x: <number>this.xValue[1] + this.dimensions.width, y: <number>this.yValue[1] + (this.dimensions.height) + Certificate.pageMargin  }
        ]
      }
    }
  }
  // [topLeftCorner, bottomRightCorner]
  // Page bounds in download mode: rendered without spacing between pages and other elements
  get downloadPageBounds(){
    if (this.certificate.size === CertificateSize.Letter) {
      return [
        { x: <number>this.downloadXValue, y: <number>this.downloadYValue },
        { x: <number>this.downloadXValue + this.dimensions.width, y: <number>this.downloadYValue + this.dimensions.height }
      ]
    }
    else {
      if (this.certificate.orientation === CertificateOrientation.Portrait){
        return [
          { x: <number>this.downloadXValue[0], y: <number>this.downloadYValue[0] },
          { x: <number>this.downloadXValue[1] + (this.dimensions.width), y: <number>this.downloadYValue[1] + this.dimensions.height }
        ]
      } else {
        return [
          { x: <number>this.downloadXValue[0], y: <number>this.downloadYValue[0] },
          { x: <number>this.downloadXValue[1] + this.dimensions.width, y: <number>this.downloadYValue[1] + this.dimensions.height  }
        ]
      }
    }
  }

  get xBounds(): number[] {
    return this.pageBounds.map(bound => bound.x);
  }

  get yBounds(): number[] {
    return this.pageBounds.map(bound => bound.y);
  }

  get downloadXValue(): number | number[] {
    if (this.certificate.size === CertificateSize.Letter) {
      return 0
    }
    //1 page of a card is both the front and the back
    else {
      const nextPageXOffset =  this.dimensions.width;
      if (this.certificate.orientation === CertificateOrientation.Landscape) {
        return [0, 0];
      } else {
        return [0, nextPageXOffset];
      }
    }
  }

  get cardSidesAreVerticallyStacked(): boolean{
    return this.certificate.size === CertificateSize.Card && (this.certificate.format === CertificateFormat.Card || (this.certificate.format === CertificateFormat.FoldOver && this.certificate.orientation === CertificateOrientation.Landscape))
  }

  get downloadYValue(): number | number[] {
    let index = this.cardSidesAreVerticallyStacked ? (this.pageIndex*2)  : (this.pageIndex)
    let y = this.dimensions.height * index
    if (this.certificate.size === CertificateSize.Letter) {
      return y;
    }
    //1 page of a card is both the front and the back
    else if (this.certificate.size === CertificateSize.Card) {
      let y2;
      if (this.certificate.orientation === CertificateOrientation.Landscape) {
        y2 = y + this.dimensions.height;
      } else {
        y2 = y;
      }
      return [y, y2];
    }
  }

  get xValue(): number | number[] {
    if (this.certificate.size === CertificateSize.Letter) {
      return Certificate.canvasXMargin;
    }
    //1 page of a card is both the front and the back
    else {
      const nextPageXOffset = Certificate.initialOffset + Certificate.pageMargin + this.dimensions.width;
      if (this.certificate.orientation === CertificateOrientation.Landscape) {
        return [Certificate.canvasXMargin, Certificate.canvasXMargin];
      } else {
        return [Certificate.canvasXMargin, nextPageXOffset];
      }
    }
  }

  get yValue(): number | number[] {
    let index = (this.certificate.size === CertificateSize.Card && this.certificate.orientation === CertificateOrientation.Landscape) ? (this.pageIndex*2)  : (this.pageIndex)
    let y = Certificate.initialOffset + (Certificate.pageMargin + this.dimensions.height) * (index);
    if (this.certificate.pageElements.length > 1 || this.certificate.size === CertificateSize.Card) {
      y += Certificate.tabHeight;
    }
    if (this.certificate.size === CertificateSize.Letter) {
      return y;
    }
    //1 page of a card is both the front and the back
    else if (this.certificate.size === CertificateSize.Card) {
      let y2;
      if (this.certificate.orientation === CertificateOrientation.Landscape) {
        y2 = y + (Certificate.pageMargin + this.dimensions.height);
      } else {
        y2 = y;
      }
      return [y, y2];
    }
  }

  get pageConfigs(): object[] {
    let baseConfig = Object.assign({}, PageCertificateElement.baseConfigs);
    baseConfig.width = `${this.dimensions.width}px`;
    baseConfig.height = `${this.dimensions.height}px`;
    if (this.highlight) baseConfig.backgroundColor = "#fefbf1";
    if (this.isLastPage && this.certificate.size !==  CertificateSize.Card ) baseConfig.marginBottom = Certificate.pageMargin + 'px';
    if (this.certificate.size === CertificateSize.Letter) {
      const hasMultiplePages = this.certificate.pageElements.length > 1;
      const marginTop = hasMultiplePages ? Certificate.pageMargin + 'px' : Certificate.initialOffset + 'px';
      baseConfig.marginTop = marginTop;
      baseConfig.marginLeft = Certificate.canvasXMargin + 'px';
      return [baseConfig];
    } else {
      let page1 = baseConfig;
      const page2 = Object.assign({}, baseConfig);
      const pages = [page1, page2];
      pages.forEach((page, index) => {

        page.marginTop = Certificate.pageMargin + 'px';
        const isBackPage = index === 1;
        if (isBackPage){
          page.marginLeft = Certificate.pageMargin + 'px';
        }else{
          page.marginLeft = Certificate.canvasXMargin + 'px'
        }
        if (isBackPage) page.marginRight = Certificate.pageMargin + 'px';
        if (isBackPage && this.isLastPage) page.marginBottom = Certificate.pageMargin + 'px';
      });
      return pages;
    }
  }
  printConfigs(scale) {
    let baseConfigs = {
      width: this.dimensions.width * scale,
      height: this.dimensions.height * scale,
      x: 0,
      y: 0,
    };
    if (this.certificate.size === CertificateSize.Letter) {
      return [baseConfigs];
    } else {
      let page1 = baseConfigs;
      const page2 = Object.assign({}, baseConfigs);
      let pages = [page1, page2];

      // flip if card certificate is both fold over and landscape
      if (this.certificate.format === CertificateFormat.FoldOver && this.certificate.orientation === CertificateOrientation.Landscape) {
        pages[1]["offsetY"] = pages[1].height;
        pages[1]["scaleY"] = -1;
      }
      return pages;
    }
  }

  toggleHighlight(): void {
    this.highlight = !this.highlight;
  }

  highlightPage(): void {
    this.highlight = true;
  }

  unHighlightPage(): void {
    this.highlight = false;
  }

  get isLastPage(): boolean {
    const lastPageIndex = this.certificate.pageElements.length - 1;
    return this.certificate.pageElements.indexOf(this) === lastPageIndex;
  }

  get foundationLayerElements(): CertificateElement[] {
    const elements = [];
    for (const layer of this.certificate.foundationLayers) {
      if (layer.pages.includes(this.client_id)) {
        const sourceDoc = this.certificate.foundationLayerSourceDocuments.find(doc => (doc.certificate_id || doc.id) === layer.source_document);
        if (!sourceDoc) continue;
        const sourcePage = sourceDoc.pageElements.find(page => page.client_id === layer.source_page);
        elements.push(...sourcePage.certificateElementsArray);
      }
    }
    return elements;
  }

}
